/**
 * EmptyAiToolsTile — what an AI tool page shows in place of itself when
 * canUseAiTools(whoami) is false. That is true in three situations, and
 * each must read as what it is:
 *
 *   - whoami has not arrived yet (a fresh load or reload of a tool URL):
 *     the page is still signing the viewer in. Every tool page renders
 *     this tile in that moment, so it says so, and never a refusal.
 *   - whoami failed (the shell recorded the error): the viewer's access
 *     could not be checked. Reload, then the system contact.
 *   - the role holds no use_ai_tools: the role-keyed explanation.
 *
 * No per-person names hardcoded. Role-keyed explanations only.
 */

import { type ReactNode } from 'react';
import { useTenant } from '@/lib/auth';
import type { UserTenantRole } from '@/lib/capabilities';
import { useSupportContact } from '@/hooks/useSupportContact';
import { contactSentence } from '@/lib/errorCopy';

const ROLE_EXPLANATION: Record<UserTenantRole, string> = {
  owner:
    "You should have AI Tools, but they aren't showing. Tell {contact_admin} so they can fix it.",
  data_steward:
    "You should have AI Tools, but they aren't showing. Tell {contact_admin} so they can fix it.",
  admin_support:
    "You should have AI Tools, but they aren't showing. Tell {contact_admin} so they can fix it.",
  supervising_cm:
    "You should have AI Tools, but they aren't showing. Tell {contact_admin} so they can fix it.",
  primary_cm:
    "You should have AI Tools, but they aren't showing. Tell {contact_admin} so they can fix it.",
  mso_staff:
    "AI Tools are for care staff, and your access is view-only. Ask {contact_admin} if you need them.",
  exec_support:
    "You should have AI Tools, but they aren't showing. Tell {contact_admin} so they can fix it.",
  board_observer:
    "AI Tools write new content, and board access is view-only. Ask {contact_admin} if you need them.",
  directory_editor:
    "AI Tools write about clients, so they aren't part of Directory Editor access. Ask {contact_admin} if you need them.",
  accounting:
    "AI Tools write about clients, so they aren't part of Accounting access. Ask {contact_admin} if you need them.",
};

export function EmptyAiToolsTile(): ReactNode {
  const { whoami, error } = useTenant();
  // Cache-only: a tile that turns a person away fetches nothing for them.
  const contact = useSupportContact({ enabled: false });
  if (whoami === null && error === null) {
    return (
      <p
        role="status"
        className="py-6 text-center text-sm text-ink-soft"
        data-testid="ai-tools-signing-in"
      >
        Signing you in…
      </p>
    );
  }
  const role = (whoami?.role ?? null) as UserTenantRole | null;
  const known = role !== null && role in ROLE_EXPLANATION;
  const heading = known
    ? 'AI Tools are not currently available for your role. Contact the office if you need assistance.'
    : "Your access couldn't be checked just now.";
  const explanation = contactSentence(
    known ? ROLE_EXPLANATION[role] : 'Reload the page, and if it happens again, tell {contact_system}.',
    contact,
  );

  return (
    <section
      className="bg-white border border-sage-800/[8%] rounded-xl shadow-sm p-6 text-center"
      data-testid="empty-ai-tools-tile"
    >
      <h2 className="text-lg font-semibold text-ink">{heading}</h2>
      <p className="text-sm text-ink-soft mt-3 max-w-lg mx-auto leading-relaxed">
        {explanation}
      </p>
    </section>
  );
}
